import { ReportMarkdown } from "@/components/ui/report/ReportMarkdown";
import {
  type ReportTextStyle,
  resolveReportTextStyle,
} from "@/components/ui/report/reportPresentation";
import { cn } from "@/lib/utils";

export interface ArticleItemProps {
  headline: string;
  sourceName: string;
  sourceUrl: string;
  body: string;
  headlineStyle?: ReportTextStyle | null;
  bodyStyle?: ReportTextStyle | null;
  className?: string;
}

const isHttpUrl = (value: string): boolean => {
  try {
    const { protocol } = new URL(value);
    return protocol === "https:" || protocol === "http:";
  } catch {
    return false;
  }
};

/** Single story block: headline, linked source, and body copy. */
export const ArticleItem = ({
  headline,
  sourceName,
  sourceUrl,
  body,
  headlineStyle,
  bodyStyle,
  className,
}: ArticleItemProps) => {
  if (headline.trim().length === 0) {
    return null;
  }

  const trimmedSourceUrl = sourceUrl.trim();
  const hasSourceName = sourceName.trim().length > 0;
  const hasSafeSourceUrl = isHttpUrl(trimmedSourceUrl);
  const hasBody = body.trim().length > 0;

  return (
    <article className={cn("flex flex-col gap-1", className)}>
      <h3
        className="m-0 text-sm font-semibold text-gray-900"
        style={resolveReportTextStyle(headlineStyle)}
      >
        {headline}
      </h3>
      {hasSourceName && hasSafeSourceUrl ? (
        <a
          href={trimmedSourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="m-0 w-fit text-xs font-normal leading-snug text-blue-600"
        >
          {sourceName}
        </a>
      ) : hasSourceName ? (
        <span className="m-0 w-fit text-xs font-normal leading-snug text-gray-900">
          {sourceName}
        </span>
      ) : null}
      {hasBody ? (
        <ReportMarkdown className="pt-1.5" textStyle={bodyStyle}>
          {body}
        </ReportMarkdown>
      ) : null}
    </article>
  );
};
